/**
 * Pure helpers for `JwtDecodeTool`. Tests live in
 * `tests/web/jwtDecodeTool.test.ts`.
 *
 * Scope:
 *   - Decode the three base64url segments of a JWS compact serialization.
 *   - Auto-strip a leading `Bearer ` prefix and surrounding whitespace.
 *   - Return parsed JSON when the segment is JSON (the common case for
 *     header/payload), otherwise the raw decoded text.
 *
 * Out of scope (different tool — `jwt-sign` is on the P1 roadmap):
 *   - Signature verification. We deliberately ignore `alg` and never
 *     fetch keys; the UI surfaces this as a yellow notice so users
 *     don't mistake "decoded" for "trusted".
 *   - JWE (encrypted) tokens — only JWS compact form is parsed here.
 */

export interface DecodedJwt {
  /** Parsed JSON header, or the raw decoded string if the header is not valid JSON. */
  header: unknown;
  /** Parsed JSON payload, or the raw decoded string if the payload is not valid JSON. */
  payload: unknown;
  /** Raw signature bytes encoded as lowercase hex; '' when the JWT has no signature. */
  signatureHex: string;
  /** Original base64url segments (after Bearer / whitespace stripping). */
  raw: { header: string; payload: string; signature: string };
  /** Non-fatal observations the UI can surface as yellow toasts / chips. */
  warnings: string[];
}

/**
 * Decode a JWT. Throws with a stable, user-readable Chinese message
 * on structural errors so the page can render `e.message` directly.
 */
export function decodeJwt(input: string): DecodedJwt {
  const warnings: string[] = [];

  // 1. Strip whitespace + optional Bearer prefix the user copied from
  // an Authorization header.
  let token = input.trim();
  const bearerMatch = /^Bearer\s+/i.exec(token);
  if (bearerMatch) {
    token = token.slice(bearerMatch[0].length).trim();
    warnings.push('已自动去除 Bearer 前缀');
  }

  if (token.length === 0) {
    throw new Error('请输入 JWT');
  }

  // 2. Split into header.payload.signature. JWS compact form has
  // exactly two dots; signature segment may be empty (some OAuth
  // ID tokens for clients with no signing).
  const segs = token.split('.');
  if (segs.length !== 3) {
    throw new Error(`JWT 应有 3 段（header.payload.signature），实际 ${segs.length} 段`);
  }
  const [rawHeader, rawPayload, rawSig] = segs;
  if (rawHeader.length === 0) throw new Error('header 段为空');
  if (rawPayload.length === 0) throw new Error('payload 段为空');

  // 3. Decode header / payload. JWT base64url is mandatory per RFC 7519.
  const header = decodeSegment(rawHeader, 'header', warnings);
  const payload = decodeSegment(rawPayload, 'payload', warnings);

  // 4. Signature is opaque bytes (often a digest); render as hex so a
  // user can paste it into another tool. Empty signature is legal.
  let signatureHex = '';
  if (rawSig.length > 0) {
    try {
      const bytes = base64UrlToBytes(rawSig);
      signatureHex = bytesToHex(bytes);
    } catch {
      throw new Error('signature 段不是合法的 base64url');
    }
  } else {
    warnings.push('signature 段为空（unsigned token）');
  }

  return {
    header,
    payload,
    signatureHex,
    raw: { header: rawHeader, payload: rawPayload, signature: rawSig },
    warnings,
  };
}

function decodeSegment(seg: string, label: 'header' | 'payload', warnings: string[]): unknown {
  let bytes: Uint8Array;
  try {
    bytes = base64UrlToBytes(seg);
  } catch {
    throw new Error(`${label} 段不是合法的 base64url`);
  }
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new Error(`${label} 段不是合法的 UTF-8`);
  }
  // Both header and payload are JSON in standards-compliant tokens
  // but malformed producers exist; surface the raw string when
  // JSON.parse fails so users can still see what's there.
  try {
    return JSON.parse(text);
  } catch {
    warnings.push(`${label} 段不是 JSON（已展示解码后的原文）`);
    return text;
  }
}

/** RFC 4648 §5 base64url decode → bytes. Tolerant of missing padding. */
export function base64UrlToBytes(input: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]*$/.test(input)) {
    throw new Error('non-base64url character');
  }
  // Re-pad to a multiple of 4 with '=' so atob (standard base64) can
  // decode it. Replace url-safe substitutions with the standard set.
  const standard = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = (4 - (standard.length % 4)) % 4;
  const padded = standard + '='.repeat(pad);

  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    s += bytes[i].toString(16).padStart(2, '0');
  }
  return s;
}

/**
 * Format a Unix epoch (seconds, as JWT `exp` / `iat` / `nbf` use)
 * into a human-readable UTC string. Returns `undefined` for values
 * that aren't finite numbers — those show as raw JSON instead of a
 * misleading "Invalid Date".
 */
export function formatJwtTime(seconds: unknown): string | undefined {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return undefined;
  const date = new Date(seconds * 1000);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
}
